import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { EyeIcon, EyeOffIcon } from './icons'
import IconButton from './IconButton'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  iconStart?: ReactNode
  iconEnd?: ReactNode
  invalid?: boolean
  /** Sign-in variant: translucent fill over the aurora background (`Login.jpg`). */
  onDark?: boolean
  shellClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { iconStart, iconEnd, invalid = false, onDark = false, shellClassName = '', className = '', disabled, ...rest },
  ref,
) {
  const shell = [
    'input-shell',
    onDark ? 'input-shell--onDark' : '',
    invalid ? 'input-shell--invalid' : '',
    disabled ? 'input-shell--disabled' : '',
    shellClassName,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={shell}>
      {iconStart && <span className="input-shell__icon">{iconStart}</span>}
      <input
        ref={ref}
        className={`input-shell__control ${className}`.trim()}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        {...rest}
      />
      {iconEnd && <span className="input-shell__icon">{iconEnd}</span>}
    </div>
  )
})

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'iconEnd'> {}

/**
 * Password field with a reveal toggle, as drawn in `Login.jpg`.
 *
 * The toggle is a real button so it is reachable by keyboard; `aria-pressed` states whether the
 * password is currently visible, which matters because the eye glyph alone does not say.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  props,
  ref,
) {
  const [visible, setVisible] = useState(false)
  return (
    <Input
      {...props}
      ref={ref}
      type={visible ? 'text' : 'password'}
      iconEnd={
        <IconButton
          label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          icon={visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
          onClick={() => setVisible((current) => !current)}
          style={{ width: 26, height: 26, color: 'inherit', background: 'none' }}
        />
      }
    />
  )
})
